import React, { useEffect, useState, useRef } from 'react';
import { MenuIcon, XIcon, UserIcon, ArrowRight, Download } from 'lucide-react';
import { Button } from './Button';
import { useModal } from './ModalContext';
interface CustomNavItem {
  name: string;
  anchor: string;
}
interface CustomAction {
  type: 'link' | 'button';
  label: string;
  href?: string;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'outline';
  icon?: React.ReactNode;
}
interface HeaderProps {
  isStatic?: boolean;
  customNavItems?: CustomNavItem[];
  customActions?: CustomAction[];
  hideMobileMenu?: boolean;
}
export const Header = ({
  isStatic = false,
  customNavItems,
  customActions,
  hideMobileMenu = false
}: HeaderProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showLogo, setShowLogo] = useState(isStatic);
  const [isScrolled, setIsScrolled] = useState(false);
  const {
    openSignupModal,
    openJoinPilotModal
  } = useModal();
  const desktopPilotButtonRef = useRef<HTMLButtonElement>(null);
  const mobilePilotButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const handleScroll = () => {
      // If header is static, always show logo regardless of scroll position
      if (!isStatic) {
        const heroLogo = document.getElementById('hero-logo');
        if (heroLogo) {
          const rect = heroLogo.getBoundingClientRect();
          setShowLogo(rect.bottom < 0);
        }
      }
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isStatic]);
  const handleOpenJoinPilotModal = (e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    openJoinPilotModal(centerX, centerY);
  };
  // Handle navigation with proper scrolling
  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, anchor: string) => {
    e.preventDefault();
    const element = document.getElementById(anchor);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
    // Close mobile menu if open
    if (isMenuOpen) {
      setIsMenuOpen(false);
    }
  };
  // Create links with proper routing and handling
  const navItems = customNavItems || [{
    name: 'What Is Nisa',
    anchor: 'what-is-nisa'
  }, {
    name: 'Features',
    anchor: 'features'
  }, {
    name: 'Why Nisa Works',
    anchor: 'why-nisa-works'
  }];
  const defaultActions: CustomAction[] = [{
    type: 'button',
    label: 'Login',
    href: 'http://app.nisa.coach/',
    variant: 'outline',
    icon: <UserIcon className="w-5 h-5 mr-2" data-id="element-98" />
  }, {
    type: 'button',
    label: 'Join the Nisa pilot',
    href: 'https://app.nisa.coach/pilot-program',
    variant: 'primary',
    icon: <ArrowRight className="w-5 h-5 ml-2" data-id="element-99" />
  }];
  const actions = customActions || defaultActions;
  return <>
      {/* Desktop Header */}
      <header className={`sticky top-0 z-40 w-full transition-all duration-300 md:block hidden ${isScrolled ? 'bg-white/90 backdrop-blur-md border-b border-cyan-100/30 shadow-sm' : 'bg-transparent'}`} data-id="element-100">
        <div className="container mx-auto px-6 h-20 flex items-center justify-between relative" data-id="element-101">
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-50/60 via-cyan-100/20 to-blue-50/50 opacity-50 -z-10" data-id="element-102" />
          <div className="flex items-center relative z-10" data-id="element-103">
            <a href="https://nisa.coach" className={`h-8 transition-all duration-300 ${showLogo || isStatic ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'}`} data-id="element-104">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jP7ueorRtM9x7giMYpBsys/nisa-capriola-logotype.png" alt="Nisa" className="h-full w-auto" data-id="element-105" />
            </a>
          </div>
          <nav className="flex items-center space-x-8" data-id="element-106">
            <div className="flex items-center space-x-8" data-id="element-107">
              {navItems.map((item, index) => <a key={index} href={`#${item.anchor}`} onClick={e => handleNavigation(e, item.anchor)} className="text-cyan-700 hover:text-cyan-900 transition-colors duration-300" data-id="element-108">
                  {item.name}
                </a>)}
            </div>
            <div className="flex items-center gap-16" data-id="element-109">
              {actions.map((action, index) => {
              if (action.type === 'link') {
                return <a key={index} href={action.href} onClick={action.onClick} className="text-cyan-700 hover:text-cyan-900 transition-colors duration-300" data-id="element-110">
                      {action.label}
                    </a>;
              }
              return <a key={index} href={action.href} data-id="element-111">
                    <Button variant={action.variant || 'primary'} size="sm" onClick={action.onClick} data-id="element-112">
                      {action.icon}
                      {action.label}
                    </Button>
                  </a>;
            })}
            </div>
          </nav>
        </div>
      </header>

      {/* Mobile Floating Menu Button - Hidden if hideMobileMenu is true */}
      {!hideMobileMenu && <button className="md:hidden fixed top-6 right-6 z-50 w-12 h-12 flex items-center justify-center rounded-xl bg-white/50 backdrop-blur-md shadow-lg transition-all duration-300 hover:bg-white/60" onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Toggle menu" data-id="element-113">
          <div className="relative flex items-center justify-center w-6 h-6" data-id="element-114">
            <span className={`absolute w-6 h-0.5 bg-gray-600 transform transition-all duration-300 ${isMenuOpen ? 'rotate-45' : '-translate-y-2'}`} data-id="element-115" />
            <span className={`absolute w-6 h-0.5 bg-gray-600 transition-opacity duration-300 ${isMenuOpen ? 'opacity-0' : 'opacity-100'}`} data-id="element-116" />
            <span className={`absolute w-6 h-0.5 bg-gray-600 transform transition-all duration-300 ${isMenuOpen ? '-rotate-45' : 'translate-y-2'}`} data-id="element-117" />
          </div>
        </button>}

      {/* Mobile Navigation Menu - Hidden if hideMobileMenu is true */}
      {!hideMobileMenu && <div className={`md:hidden fixed inset-0 backdrop-blur-lg bg-cyan-50/95 transition-all duration-500 ease-in-out ${isMenuOpen ? 'opacity-100 pointer-events-auto translate-x-0' : 'opacity-0 pointer-events-none translate-x-full'}`} style={{
      zIndex: 40
    }} data-id="element-118">
          <div className="container mx-auto px-6 py-8" data-id="element-119">
            {/* Nisa Logo at top left */}
            <a href="https://nisa.coach" className="inline-block mb-12" data-id="element-120">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jP7ueorRtM9x7giMYpBsys/nisa-capriola-logotype.png" alt="Nisa" className="h-10 w-auto" data-id="element-121" />
            </a>

            <nav className="flex flex-col" data-id="element-122">
              {/* Primary CTA Buttons */}
              <div className="space-y-4 mb-8" data-id="element-123">
                {actions.map((action, index) => {
              if (action.type === 'button') {
                return <a key={index} href={action.href} className="block" data-id="element-124">
                        <Button variant={action.variant || 'primary'} fullWidth size="lg" onClick={action.onClick} data-id="element-125">
                          {action.icon}
                          {action.label}
                        </Button>
                      </a>;
              }
              return null;
            })}
                {actions.map((action, index) => {
              if (action.type === 'link') {
                return <a key={index} href={action.href} className="block" data-id="element-126">
                        <Button variant="outline" fullWidth size="lg" onClick={action.onClick} data-id="element-127">
                          {action.label}
                        </Button>
                      </a>;
              }
              return null;
            })}
              </div>

              {/* Navigation Links (if any) */}
              {navItems.length > 0 && <div className="space-y-3 pt-6 border-t border-cyan-200" data-id="element-128">
                  {navItems.map((item, index) => <a key={index} href={`#${item.anchor}`} onClick={e => handleNavigation(e, item.anchor)} className="block text-base text-cyan-700 hover:text-cyan-900 transition-all duration-300 py-2 px-4 rounded-lg hover:bg-cyan-100 active:bg-cyan-200" data-id="element-129">
                      {item.name}
                    </a>)}
                </div>}
            </nav>
          </div>
        </div>}
    </>;
};